import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gauge, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { normalizeEmail } from '@/services/authClient';
import {
  submitTenantRegistration,
  TenantRegistrationApiError,
} from '@/services/tenantRegistrationClient';

function isValidEmail(value) {
  const v = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** Client-side policy; backend may return stricter field errors on 400. */
function isReasonablePassword(p) {
  if (p.length < 8) return false;
  if (!/[a-zA-Z]/.test(p) || !/[0-9]/.test(p)) return false;
  return true;
}

export default function SetupPage() {
  const { t, lang, pathFor, toggleLang } = useLanguage();
  const s = t.setup;
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerPasswordConfirm, setOwnerPasswordConfirm] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [companyFullName, setCompanyFullName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [licenseNo, setLicenseNo] = useState('');
  const [operationNo, setOperationNo] = useState('');
  const [notes, setNotes] = useState('');

  const [clientErrors, setClientErrors] = useState({});
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const clearFieldError = (key) => {
    setClientErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setServerFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateClient = () => {
    const errs = {};
    if (!companyName.trim()) {
      errs.company_name = lang === 'EN' ? 'Required' : 'Gerekli';
    }
    if (!ownerEmail.trim() || !isValidEmail(ownerEmail)) {
      errs.owner_email = lang === 'EN' ? 'Valid email required' : 'Geçerli e-posta gerekli';
    }
    if (!isReasonablePassword(ownerPassword)) {
      errs.owner_password = s.passwordWeak;
    }
    if (ownerPassword !== ownerPasswordConfirm) {
      errs.owner_password_confirm = s.passwordMismatch;
    }
    return errs;
  };

  const buildPayload = () => {
    /** @type {import('@/services/tenantRegistrationClient').TenantRegistrationRequest} */
    const payload = {
      company_name: companyName.trim(),
      owner_email: normalizeEmail(ownerEmail),
      owner_password: ownerPassword,
      owner_password_confirm: ownerPasswordConfirm,
    };
    const trim = (v) => String(v || '').trim();
    if (trim(companyFullName)) payload.company_full_name = trim(companyFullName);
    if (trim(companyAddress)) payload.company_address = trim(companyAddress);
    if (trim(contactPhone)) payload.contact_phone = trim(contactPhone);
    if (trim(licenseNo)) payload.license_no = trim(licenseNo);
    if (trim(operationNo)) payload.operation_no = trim(operationNo);
    if (trim(notes)) payload.notes = trim(notes);
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setServerFieldErrors({});
    const errs = validateClient();
    if (Object.keys(errs).length) {
      setClientErrors(errs);
      return;
    }
    setClientErrors({});
    setSubmitting(true);
    try {
      const result = await submitTenantRegistration(buildPayload());
      if (result.id && result.status_token) {
        const q = new URLSearchParams({ token: result.status_token });
        navigate(`${pathFor(`/setup/status/${encodeURIComponent(result.id)}`)}?${q.toString()}`, {
          state: { derived_schema_preview: result.derived_schema_preview },
        });
        return;
      }
      setFormError(lang === 'EN' ? 'Unexpected response from server.' : 'Sunucudan beklenmeyen yanıt.');
    } catch (err) {
      if (err instanceof TenantRegistrationApiError) {
        if (err.status === 429) {
          setFormError(err.message);
        } else if (err.status === 400 && Object.keys(err.fieldErrors).length) {
          setServerFieldErrors(err.fieldErrors);
          setFormError(err.message);
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError(err?.message || (lang === 'EN' ? 'Registration failed.' : 'Kayıt başarısız.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (key) => clientErrors[key] || serverFieldErrors[key];

  return (
    <div className="min-h-screen flex" data-testid="setup-page">
      <div className="hidden lg:flex lg:w-[52%] bg-[#0D0303] flex-col p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 48px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 48px)',
          }}
        />
        <div className="absolute top-0 left-0 w-1 h-full bg-[#A11212]" />

        <Link to={pathFor('/')} className="flex items-center gap-3 relative z-10" data-testid="setup-logo">
          <div className="w-9 h-9 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <span
            className="font-black text-xl text-white tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            CarniTrack
          </span>
        </Link>

        <div className="flex-1 flex flex-col justify-center relative z-10 max-w-sm">
          <div className="w-8 h-[3px] bg-[#A11212] mb-8" />
          <h2
            className="text-4xl xl:text-5xl font-black text-white leading-tight tracking-tighter mb-5"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {lang === 'EN' ? (
              <>
                Track every animal.
                <br />
                <span className="text-[#D32F2F]">Log every step.</span>
              </>
            ) : (
              <>
                Her hayvanı takip edin.
                <br />
                <span className="text-[#D32F2F]">Her adımı kaydedin.</span>
              </>
            )}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">{t.hero.subtext}</p>

          <div className="mt-8 border border-white/10 bg-white/5 p-4">
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">{s.slugInfoTitle}</p>
            <p className="text-sm text-gray-300 leading-relaxed">{s.slugInfoBody}</p>
          </div>
        </div>

        <div className="relative z-10 flex gap-10 pt-6 border-t border-white/10">
          {[
            { num: '500+', label: lang === 'EN' ? 'Facilities' : 'Tesis' },
            { num: '2M+', label: lang === 'EN' ? 'Animals Tracked' : 'Takip Edilen Hayvan' },
          ].map((stat) => (
            <div key={stat.label}>
              <div
                className="text-2xl font-black text-white"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {stat.num}
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-white flex flex-col items-center justify-center px-8 py-12 relative overflow-y-auto">
        <Link to={pathFor('/')} className="flex items-center gap-2 mb-8 lg:hidden" data-testid="setup-mobile-logo">
          <div className="w-8 h-8 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-4 h-4 text-white" />
          </div>
          <span
            className="font-black text-lg text-gray-900"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            CarniTrack
          </span>
        </Link>

        <div className="w-full max-w-[380px]">
          <h1
            className="text-2xl font-black text-gray-900 tracking-tighter mb-1"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {s.title}
          </h1>
          <p className="text-sm text-gray-500 mb-7">{s.subtitle}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                {s.companyName}
              </label>
              <input
                data-testid="company-name-input"
                value={companyName}
                onChange={(ev) => {
                  setCompanyName(ev.target.value);
                  clearFieldError('company_name');
                  setFormError('');
                }}
                placeholder={s.companyNamePlaceholder}
                autoComplete="organization"
                className={`w-full h-11 border ${
                  fieldError('company_name') ? 'border-red-400' : 'border-gray-200'
                } bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all`}
              />
              {fieldError('company_name') && (
                <p className="text-xs text-red-500 mt-1">{fieldError('company_name')}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                {s.ownerEmail}
              </label>
              <input
                data-testid="owner-email-input"
                type="email"
                value={ownerEmail}
                onChange={(ev) => {
                  setOwnerEmail(ev.target.value);
                  clearFieldError('owner_email');
                  setFormError('');
                }}
                placeholder={s.emailPlaceholder}
                autoComplete="email"
                className={`w-full h-11 border ${
                  fieldError('owner_email') ? 'border-red-400' : 'border-gray-200'
                } bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all`}
              />
              {fieldError('owner_email') && (
                <p className="text-xs text-red-500 mt-1">{fieldError('owner_email')}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                {s.password}
              </label>
              <input
                data-testid="owner-password-input"
                type="password"
                value={ownerPassword}
                onChange={(ev) => {
                  setOwnerPassword(ev.target.value);
                  clearFieldError('owner_password');
                  setFormError('');
                }}
                placeholder={s.passwordPlaceholder}
                autoComplete="new-password"
                className={`w-full h-11 border ${
                  fieldError('owner_password') ? 'border-red-400' : 'border-gray-200'
                } bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all`}
              />
              {fieldError('owner_password') && (
                <p className="text-xs text-red-500 mt-1">{fieldError('owner_password')}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                {s.passwordConfirm}
              </label>
              <input
                data-testid="owner-password-confirm-input"
                type="password"
                value={ownerPasswordConfirm}
                onChange={(ev) => {
                  setOwnerPasswordConfirm(ev.target.value);
                  clearFieldError('owner_password_confirm');
                  setFormError('');
                }}
                placeholder={s.passwordConfirmPlaceholder}
                autoComplete="new-password"
                className={`w-full h-11 border ${
                  fieldError('owner_password_confirm') ? 'border-red-400' : 'border-gray-200'
                } bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all`}
              />
              {fieldError('owner_password_confirm') && (
                <p className="text-xs text-red-500 mt-1">{fieldError('owner_password_confirm')}</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowOptional((v) => !v)}
              className="text-xs text-[#A11212] font-semibold hover:underline w-full text-left"
            >
              {showOptional ? s.optionalToggleHide : s.optionalToggleShow}
            </button>

            {showOptional && (
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {s.optionalDetails}
                </p>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.companyFullName}
                  </label>
                  <input
                    value={companyFullName}
                    onChange={(e) => setCompanyFullName(e.target.value)}
                    placeholder={s.companyFullNamePlaceholder}
                    className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.companyAddress}
                  </label>
                  <textarea
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder={s.companyAddressPlaceholder}
                    rows={2}
                    className="w-full border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.contactPhone}
                  </label>
                  <input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder={s.contactPhonePlaceholder}
                    className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.licenseNo}
                  </label>
                  <input
                    value={licenseNo}
                    onChange={(e) => setLicenseNo(e.target.value)}
                    placeholder={s.licenseNoPlaceholder}
                    className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.operationNo}
                  </label>
                  <input
                    value={operationNo}
                    onChange={(e) => setOperationNo(e.target.value)}
                    placeholder={s.operationNoPlaceholder}
                    className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.notes}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={s.notesPlaceholder}
                    rows={2}
                    className="w-full border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212]"
                  />
                </div>
              </div>
            )}

            {formError && (
              <p className="text-xs text-red-600" data-testid="setup-form-error">
                {formError}
              </p>
            )}

            <button
              type="submit"
              data-testid="submit-registration-btn"
              disabled={submitting}
              className="w-full h-11 bg-[#A11212] hover:bg-[#8B0F0F] disabled:bg-[#A11212]/60 text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
            >
              {submitting ? s.submitting : s.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-sm text-gray-500 mt-5 text-center">
            {s.hasAccount}{' '}
            <Link to={pathFor('/signin')} className="text-[#A11212] font-semibold hover:underline" data-testid="signin-link">
              {t.nav.signin}
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={toggleLang}
          data-testid="lang-toggle"
          className="absolute bottom-6 right-6 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 px-3 py-1 transition-colors"
        >
          {lang === 'EN' ? 'Türkçe' : 'English'}
        </button>
      </div>
    </div>
  );
}
